import axios from 'axios';
import { config, getApiUrl } from '../config';
import { setupResponseInterceptor, enableSchemaMonitoring } from './apiInterceptors';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: config.api.baseUrl,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: config.api.timeout,
});

// Token storage utilities - consistent with authService
const TOKEN_STORAGE_KEY = config.auth.tokenStorageKey;

const getStoredTokens = () => {
  try {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!stored) return null;
    
    const tokens = JSON.parse(stored);
    
    // Check if tokens are expired
    if (tokens.expires_at && new Date(tokens.expires_at) <= new Date()) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      return null;
    }
    
    return tokens;
  } catch (error) {
    // Failed to get tokens
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    return null;
  }
};

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    // Skip auth for login and other public endpoints
    const publicEndpoints = ['/auth/login', '/auth/refresh', '/health'];
    const isPublicEndpoint = publicEndpoints.some(endpoint => config.url?.includes(endpoint));
    
    if (!isPublicEndpoint) {
      const tokens = getStoredTokens();
      // Check both new OpenAPI format (token) and legacy format (access_token)
      const authToken = tokens?.token || tokens?.access_token;
      if (authToken) {
        config.headers.Authorization = `Bearer ${authToken}`;
        // Auth token added
      } else {
        // No auth token
      }
    } else {
      // Public endpoint
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh and common errors
apiClient.interceptors.response.use(
  (response) => {
    // Response intercepted
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Extract error message from Rails error format
    const errorMessage = error.response?.data?.error?.message || 
                        error.response?.data?.message || 
                        error.message || 
                        'An unexpected error occurred';
    
    // Error intercepted

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const tokens = getStoredTokens();
        const refreshToken = tokens?.refresh_token;
        
        if (refreshToken) {
          // Try to refresh the token - send refresh_token in body as per OpenAPI spec
          const refreshResponse = await axios.post(getApiUrl('/auth/refresh'), {
            refresh_token: refreshToken
          }, {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });
          
          // Extract tokens from Rails response format
          const responseData = refreshResponse.data?.data || refreshResponse.data;
          const newTokens = responseData;
          localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(newTokens));
          
          // Retry original request with new token
          const authToken = newTokens?.token || newTokens?.access_token;
          if (authToken) {
            originalRequest.headers.Authorization = `Bearer ${authToken}`;
            return apiClient(originalRequest);
          }
        }
      } catch (refreshError) {
        // Token refresh failed
      }
      
      // Refresh failed or no refresh token, clear storage and redirect
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      window.location.href = '/login';
    }
    
    // Return a standardized error object
    const standardizedError = {
      message: errorMessage,
      status: error.response?.status,
      details: error.response?.data?.error?.details || {},
      originalError: error
    };
    
    return Promise.reject(standardizedError);
  }
);

// Setup response validation and transformation
setupResponseInterceptor(apiClient, {
  enableValidation: process.env.NODE_ENV === 'development',
  enableTransformation: true,
  enableLogging: process.env.NODE_ENV === 'development',
  strictMode: false, // Set to true when ready for strict validation
});

// Enable schema monitoring in development
if (process.env.NODE_ENV === 'development') {
  enableSchemaMonitoring(apiClient);
}

export { apiClient, getApiUrl };